import jwt_decode from 'jwt-decode';
import t from 'typy';
import dateFormat from 'dateformat';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import Cookies from 'js-cookie';
import CryptoJS from 'crypto-js';
import countries from '../countries.json';

const secretKey = process.env.REACT_APP_CRYPTO_SECRET;
const maxLength = 20;
export const CountryList = () => {
  const countryList = useMemo(
    () => countries.map(({ name }) => name.substring(0, maxLength)),
    [],
  );
  return countryList;
};
export const NationalityList = () => {
  const nationalityList = useMemo(
    () => countries.map(({ demonym }) => demonym.substring(0, maxLength)),
    [],
  );
  return nationalityList;
};
export const SoundRecordingRightsholderTypeList = () => {
  const rightsholderList = [
    'Producer',
    'Record Company',
    'Producer/Record Company',
    'Other',
  ];
  return rightsholderList;
};
export const getError = (err) =>
  err.response && err.response.data && err.response.data.message
    ? err.response.data.message
    : err.message;
export const getCookieValue = (cookieName) => {
  const cookieValue = Cookies.get(cookieName);
  if (cookieValue) {
    return decodeURIComponent(cookieValue);
  }
  return null;
};
export const clearCookieValue = (cookieName) => {
  const cookieValue = getCookieValue(cookieName);
  if (cookieValue) {
    Cookies.remove(cookieName);
  }
  return null;
};
export const getSignupPayload = (payloadName) => {
  const savedSignupPayload = JSON.parse(sessionStorage.getItem(payloadName));
  if (!savedSignupPayload) return null;
  return savedSignupPayload;
};
export const firstLetter = (name) => {
  if (t(name).isNullOrUndefined) return '';
  return name.charAt(0);
};
export const phoneNumberMasking = (phoneNumber) => {
  if (t(phoneNumber).isNullOrUndefined) return '';
  return `${phoneNumber.slice(0, -5)}*****`;
};
export const getInitials = (name) => {
  if (t(name).isNullOrUndefined) return '';
  // Split the name into words
  const words = name.split(' ');
  // Initialize an empty string to store the initials
  let initials = '';
  // Add the initial of the first word to the initials string
  if (words.length > 0) {
    initials += words[0][0].toUpperCase();
  }
  // Add the initial of the last word to the initials string
  if (words.length > 1) {
    initials += words[words.length - 1][0].toUpperCase();
  }
  return initials;
};
// export const capitalizeFirstLetter = (name) => {
//   if (t(name).isNullOrUndefined) return '';
//   return name.charAt(0).toUpperCase() + name.slice(1);
// };
export const capitalizeFirstLetter = (str) => {
  // Check if the string is empty or null
  if (!str || str.length === 0) {
    return str; // Return the original string if it's empty or null
  }
  return str.replace(/\b\w/g, (match) => match.toUpperCase());
};
export const splitAndReturnFirstString = (str) => {
  if (t(str).isNullOrUndefined || t(str).isEmptyString) return '';
  const [first] = str.split('/');
  return first.toLowerCase();
};
// export const formatDates = (dateInput) => {
//   const dt = DateTime.fromISO(dateInput);
//   return dt.toLocaleString(DateTime.DATE_FULL);
// };
export const formatDates = (dateInput) => {
  const dt = DateTime.fromISO(dateInput);
  return dt.toLocaleString({
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};
export const formatToNewDate = (date) => {
  return dateFormat(date, 'mm/dd/yyyy');
};
export const decodeToken = (token) => {
  return jwt_decode(token);
};
export const encryptToken = async (token) => {
  const encryptedToken = CryptoJS.AES.encrypt(token, secretKey).toString();
  return encryptedToken;
};
export const decryptToken = (encryptedToken) => {
  const decryptedToken = CryptoJS.AES.decrypt(
    encryptedToken,
    secretKey,
  ).toString(CryptoJS.enc.Utf8);
  return decryptedToken;
};
export const checkAuth = () => {
  const token = getCookieValue('_session_id');
  if (!token) {
    return false; // Token doesn't exist, consider the user as not authenticated
  }
  try {
    const decryptedToken = decryptToken(token);
    const decodedToken = decodeToken(decryptedToken);
    const expirationTime = decodedToken.exp * 1000;
    const currentTime = new Date().getTime();
    if (currentTime > expirationTime) {
      clearCookieValue('_session_id');
      return false; // Token is expired, consider the user as not authenticated
    }
    return true; // Token is still valid, consider the user as authenticated
  } catch (error) {
    clearCookieValue('_session_id');
    return false; // Token error, consider the user as not authenticated
  }
};
export const getFirstNonZeroValue = (createdAt) => {
  const today = DateTime.local();
  const obj = today
    .diff(createdAt, [
      'years',
      'months',
      'weeks',
      'days',
      'hours',
      'minutes',
      'seconds',
    ])
    .toObject();
  if (obj.years > 0) {
    if (obj.years > 1) return `${obj.years} years ago`;
    return `${obj.years} year ago`;
  }
  if (obj.months > 0) {
    if (obj.months > 1) return `${obj.months} months ago`;
    return `${obj.months} month ago`;
  }
  if (obj.weeks > 0) {
    if (obj.weeks > 1) return `${obj.weeks} weeks ago`;
    return `${obj.weeks} week ago`;
  }
  if (obj.days > 0) {
    if (obj.days > 1) return `${obj.days} days ago`;
    return `${obj.days} day ago`;
  }
  if (obj.hours > 0) {
    if (obj.hours > 1) return `${obj.hours} hours ago`;
    return `${obj.hours} hour ago`;
  }
  if (obj.minutes > 0) {
    if (obj.minutes > 1) return `${obj.minutes} minutes ago`;
    return `${obj.minutes} minute ago`;
  }
  return 'a few moments ago';
};
export const generateYearsFromDate = (startYear) => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = startYear; i <= currentYear; i += 1) {
    years.unshift(i);
  }
  return years;
};
export const generatePagination = (currentPage, totalPages) => {
  // If the total number of pages is 7 or less,
  // display all pages without any ellipsis.
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  // If the current page is among the first 3 pages,
  // show the first 3, an ellipsis, and the last 2 pages.
  if (currentPage <= 3) {
    return [1, 2, 3, '...', totalPages - 1, totalPages];
  }
  // If the current page is among the last 3 pages,
  // show the first 2, an ellipsis, and the last 3 pages.
  if (currentPage >= totalPages - 2) {
    return [1, 2, '...', totalPages - 2, totalPages - 1, totalPages];
  }
  // If the current page is somewhere in the middle,
  // show the first page, an ellipsis, the current page and its neighbors,
  // another ellipsis, and the last page.
  return [
    1,
    '...',
    currentPage - 1,
    currentPage,
    currentPage + 1,
    '...',
    totalPages,
  ];
};
